// src/pages/CourseTime/components/CourseSidebar.comp.jsx
import React from 'react';
import * as S from '../styles';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

const CourseSidebar = ({
  collapsed,
  toggleSider,
  layoutLoading,
  courseTitle,
  themeColor,
  programs,
  isProgramExpanded,
  isUnitExpanded,
  isSessionExpanded,
  activeSessionNo,
  activeSegment,
  sessionContent,
  onToggleProgram,
  onToggleUnit,
  onToggleSession,
  onSessionSelect,
  onSegmentSelect,
  getSegmentLabel,
  resetActivityState,
  updateCurrentPosition,
  segmentsWithAssessments,
  pageLc
}) => {
  const isUnitCompleted = (unit) => {
    if (!unit.sessions || unit.sessions.length === 0) return false;
    return unit.sessions.every(session => session.completed === true);
  };

  const isProgramCompleted = (program) => {
    if (!program.units || program.units.length === 0) return false;
    return program.units.every(unit => isUnitCompleted(unit));
  };

  const handleSegmentClick = (segmentKey) => {
    // Immediate position update when navigating to segment
    updateCurrentPosition(activeSessionNo, segmentKey, 0, true);
    onSegmentSelect(segmentKey);
    resetActivityState(); // Reset activity state when changing segments

    if (window.innerWidth <= 768 && !collapsed) {
      toggleSider();
    }
  };

  return (
    <S.Sidebar collapsed={collapsed}>
      {layoutLoading ? (
        <S.SidebarLoading>
          <S.SpinnerSmall />
        </S.SidebarLoading>
      ) : (
        <S.ProgramContainer>
          <S.ProgramHeader>
            <S.CourseTitle collapsed={collapsed} themeColor={themeColor}>
              {courseTitle}
            </S.CourseTitle>
            <S.MobileToggleButton onClick={toggleSider}>
              {collapsed ? '☰' : '✖'}
            </S.MobileToggleButton>
          </S.ProgramHeader>
          {programs.map((program, index) => (
            <S.Program key={program.code}>
              <S.ProgramHeader
                completed={isProgramCompleted(program)}
                onClick={() => !collapsed && onToggleProgram(program.code)}
              >
                {!collapsed && (
                  <>
                    <S.ProgramTitle>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__module')} {index + 1}: {program.full_name}</S.ProgramTitle>
                                      <S.ProgramToggle>
                    {isProgramExpanded(program.code) ? '▼' : '▶'}
                  </S.ProgramToggle>
                  </>
                )}
              </S.ProgramHeader>

              {!collapsed && isProgramExpanded(program.code) && program.units && (
                <S.Units>
                  {program.units.map((unit, index) => (
                    <S.Unit key={unit.unit_no}>
                      <S.UnitHeader
                        completed={isUnitCompleted(unit)}
                        onClick={() => onToggleUnit(unit.unit_no)}
                      >
                        <S.UnitTitle>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__unit')} {index + 1}: {unit.full_name}</S.UnitTitle>
                        <S.UnitToggle>
                          {isUnitExpanded(unit.unit_no) ? '▼' : '▶'}
                        </S.UnitToggle>
                      </S.UnitHeader>

                      {isUnitExpanded(unit.unit_no) && unit.sessions && (
                        <S.SessionMenu>
                          {unit.sessions.map((session, index) => (
                            <React.Fragment key={session.session_no}>
                              <S.SessionMenuItem
                                active={activeSessionNo === session.session_no}
                                completed={session.completed}
                                themeColor={themeColor}
                                onClick={() => {
                                  onSessionSelect(session.session_no);
                                }}
                              >
                                <S.CompletedIcon className={session.completed ? 'completed' : ''}>✓</S.CompletedIcon>
                                <S.SessionTitleText themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__session')} {index + 1}: {session.full_name}</S.SessionTitleText>
                                <S.SessionToggle>
                                  {isSessionExpanded(session.session_no) ? '▼' : '▶'}
                                </S.SessionToggle>
                              </S.SessionMenuItem>

                              {isSessionExpanded(session.session_no) && activeSessionNo === session.session_no && sessionContent && (
                                <S.SessionMenu style={{ marginLeft: '16px' }}>
                                  {/* Segments now come directly from sessionContent - assessments are embedded within segments */}
                                  {(segmentsWithAssessments && segmentsWithAssessments.length > 0
                                    ? segmentsWithAssessments
                                    : Object.keys(sessionContent).filter(segmentKey =>
                                        Array.isArray(sessionContent[segmentKey]) && sessionContent[segmentKey].length > 0
                                      )
                                  ).map(segmentKey => {
                                    const segmentLabel = getSegmentLabel(segmentKey);

                                    return (
                                      <S.SessionMenuItem
                                        key={segmentKey}
                                        active={activeSegment === segmentKey}
                                        themeColor={themeColor}
                                        onClick={() => handleSegmentClick(segmentKey)}
                                      >
                                        <S.SegmentTitleText>{segmentLabel}</S.SegmentTitleText>
                                      </S.SessionMenuItem>
                                    );
                                  })}
                                </S.SessionMenu>
                              )}
                            </React.Fragment>
                          ))}
                        </S.SessionMenu>
                      )}
                    </S.Unit>
                  ))}
                </S.Units>
              )}
            </S.Program>
          ))}
        </S.ProgramContainer>
      )}
    </S.Sidebar>
  );
};

export default CourseSidebar;
